import React, { useState, useEffect } from 'react';
import { Sparkles, Loader2, ArrowRight } from 'lucide-react';
import { GoogleGenAI } from '@google/genai';
import { useApp } from '../context/AppContext';
import * as ReactRouterDOM from 'react-router-dom';

const { Link } = ReactRouterDOM as any;

export const AiCartAdvisor: React.FC = () => {
    const { cart, getAllProducts, toggleCart } = useApp();
    const [advice, setAdvice] = useState<{ message: string, suggestedProductId?: string | null }>({ message: '' });
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (cart.length === 0) {
            setAdvice({ message: '' });
            return;
        }

        const generateAdvice = async () => {
            setIsLoading(true);
            try {
                // Fetch full product details for cart items
                const allProducts = getAllProducts();
                const cartDetails = cart.map(item => {
                    const full = allProducts.find((p:any) => p.id === item.productId);
                    return {
                        title: item.title,
                        category: full?.pageId || 'Ogólne',
                        price: item.price
                    };
                });

                const catalog = allProducts.map((p: any) => ({ title: p.title, price: p.price, id: p.id })).slice(0, 20); // pass some catalog context

                const cartCount = cart.reduce((total, item) => total + item.quantity, 0);
                const cartSubtotalGross = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
                
                let discountPercent = 0;
                if (cartCount >= 3) {
                    discountPercent = 15;
                } else if (cartCount === 2) {
                    discountPercent = 10;
                }
                const cartSubtotalAfterDiscount = cartSubtotalGross - (cartSubtotalGross * discountPercent) / 100;
                const needsForFreeShipping = 500 - cartSubtotalAfterDiscount;

                const prompt = `Analizujesz koszyk klienta w sklepie. Pokaż mu, że widzisz co kupuje i zasugeruj 1 dodatkową rzecz komplementarną.
Stan koszyka: 
- Ilość produktów sztuk: ${cartCount}
- Obecny rabat ilościowy: ${discountPercent}% (Zasady: 2 produkty = 10%, 3 i więcej = 15%)
- Brakuje do darmowej dostawy (od 500 PLN): ${needsForFreeShipping > 0 ? needsForFreeShipping.toFixed(2) + ' PLN' : '0 PLN (Darmowa dostawa aktywna!)'}
Koszyk szczegóły: ${JSON.stringify(cartDetails)}.
Dostępny katalog: ${JSON.stringify(catalog)}.

Zasugeruj konkretny, pasujący produkt z "Dostępny katalog". Zmotywuj klienta tym, że dodanie produktu zwiększy rabat (jeśli ma mniej niż 3 sztuki) lub przybliży/zapewni darmową dostawę (jeśli brakuje). Zachowaj naturalny, pomocny ton doradcy. Komunikat krótki (2-3 zdania).

Zwróć odpowiedź w czystym formacie JSON:
{
  "message": "Twój komunikat tutaj",
  "suggestedProductId": "id_z_katalogu_jesli_jakis_zaproponowano_lub_null"
}`;

                const payload = {
                    model: 'gemini-3.1-flash-lite',
                    contents: [{role: 'user', parts: [{text: prompt}]}],
                    generationConfig: {
                        responseMimeType: 'application/json'
                    }
                };

                const res = await fetch('api/generate_ai.php', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });
                
                if (!res.ok) {
                    const errText = await res.text();
                    throw new Error(`API Error: ${res.status} ${errText}`);
                }
                const responseData = await res.json();
                const responseText = responseData.candidates?.[0]?.content?.parts?.[0]?.text || '{}';

                const data = JSON.parse(responseText);
                setAdvice({
                    message: data.message || 'Świetny wybór! Dziękujemy za zaufanie.',
                    suggestedProductId: data.suggestedProductId
                });
            } catch (err) {
                console.error("Cart AI context error:", err);
            } finally {
                setIsLoading(false);
            }
        };

        const timeout = setTimeout(() => {
            generateAdvice();
        }, 800); // debounce

        return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cart]);

    if (cart.length === 0) return null;

    const suggestedProduct = advice.suggestedProductId ? getAllProducts().find((p: any) => p.id === advice.suggestedProductId) : null;

    return (
        <div className="bg-indigo-50 p-4 border-l-2 border-indigo-500 text-indigo-900 mx-6 mb-6 text-sm">
            <div className="flex items-center gap-2 font-bold mb-1">
                <Sparkles size={14} className="text-indigo-600"/> Twoje zakupy
            </div>
            {isLoading ? (
                <div className="flex items-center gap-2 text-indigo-400 text-xs mt-2">
                    <Loader2 size={12} className="animate-spin" /> Analizuję Twój koszyk...
                </div>
            ) : (
                <div className="space-y-3">
                    <p className="text-xs leading-relaxed opacity-90">{advice.message}</p>
                    
                    {suggestedProduct && (
                        <div className="bg-white p-3 rounded border border-indigo-100 flex items-center gap-3 mt-2 shadow-sm">
                            {suggestedProduct.imageUrl && (
                                <img src={suggestedProduct.imageUrl} alt={suggestedProduct.title} className="w-10 h-10 object-cover rounded" />
                            )}
                            <div className="flex-1">
                                <p className="text-xs font-bold">{suggestedProduct.title}</p>
                                <p className="text-[10px] text-gray-500">{suggestedProduct.price} PLN</p>
                            </div>
                            <Link 
                                to={`/product/${suggestedProduct.id}`} 
                                onClick={toggleCart}
                                className="text-xs bg-indigo-600 text-white px-3 py-1.5 rounded hover:bg-indigo-700 transition-colors flex items-center gap-1 font-medium whitespace-nowrap"
                            >
                                Zobacz <ArrowRight size={12} />
                            </Link>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
